import { apiSlice } from './api'
import type { ApiResponse } from '../types/api'
import type { Menu, MenuFormData, MenuSortUpdateData } from '../types/menu'

// 메뉴별 권한배정 화면에서 사용하는 메뉴 트리 항목.
// selectAuthMenuTree 쿼리 결과 (apps/api-server/.../sql_Menu.xml 의 selectAuthMenuTree)
export interface AuthMenuTreeItem {
  menu_no: string
  menu_nm: string
  up_menu_no: string | null
  menu_depth: number | string
  sort_sn: number
  use_yn: string
  hmpg_se_cd?: string
  gnb_use_yn?: string
  depth1_nm?: string
  depth2_nm?: string
  depth3_nm?: string
  depth4_nm?: string
  menu_authrt_se_cd?: string
  auth_r?: string
  auth_c?: string
  auth_u?: string
  auth_d?: string
  auth_e?: string
}

export const menuApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // 관리자 네비게이션 메뉴 목록 조회 (AdminLayout 용)
    getAdminNavMenus: builder.query<ApiResponse<Menu[]>, void>({
      query: () => '/admin/menu/nav',
      providesTags: ['Menu'],
    }),

    // 권한별 메뉴 트리 조회 (메뉴별 권한배정 화면 — AuthorityMenuAssign.tsx)
    getAuthMenuTree: builder.query<
      ApiResponse<AuthMenuTreeItem[]>,
      { authrt_no: string; hmpg_se_cd?: string }
    >({
      query: ({ authrt_no, hmpg_se_cd = 'B' }) => ({
        url: '/admin/menu/auth/tree',
        params: { authrt_no, hmpg_se_cd },
      }),
      providesTags: (_result, _error, { authrt_no }) => [
        { type: 'MenuAuth', id: authrt_no },
      ],
    }),

    // 권한별 메뉴 일괄 저장 (메뉴별 권한배정 화면 — AuthorityMenuAssign.tsx)
    saveAuthMenu: builder.mutation<
      ApiResponse<void>,
      { authrt_no: string; hmpg_se_cd?: string; menu_list: string }
    >({
      query: ({ authrt_no, hmpg_se_cd = 'B', menu_list }) => ({
        url: '/admin/menu/auth',
        method: 'POST',
        body: { authrt_no, hmpg_se_cd, menu_list },
      }),
      invalidatesTags: (_result, _error, { authrt_no }) => [
        { type: 'MenuAuth', id: authrt_no },
        'Menu',
      ],
    }),

    // 메뉴 목록 조회 (트리 구조)
    getMenus: builder.query<ApiResponse<Menu[]>, { menuType?: string; useYn?: string; hmpg_se_cd?: string } | void>({
      query: (params) => ({
        url: '/admin/menu',
        params: { hmpg_se_cd: 'B', ...params },  // B: 백오피스/관리자 메뉴
      }),
      providesTags: ['Menu'],
    }),

    // 메뉴 상세 조회
    getMenuById: builder.query<ApiResponse<Menu>, string>({
      query: (menuNo) => `/admin/menu/${menuNo}`,
      providesTags: (_result, _error, menuNo) => [{ type: 'Menu', id: menuNo }],
    }),

    // 메뉴 등록
    createMenu: builder.mutation<ApiResponse<Menu>, MenuFormData>({
      query: (body) => ({
        url: '/admin/menu',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Menu'],
    }),

    // 메뉴 수정 (body는 백엔드 필드명: lnkg_addr, up_menu_no, sort_sn 등)
    updateMenu: builder.mutation<
      ApiResponse<Menu>,
      { menuNo: string; body: Record<string, unknown> }
    >({
      query: ({ menuNo, body }) => ({
        url: `/admin/menu/${menuNo}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Menu'],
    }),

    // 메뉴 삭제
    deleteMenu: builder.mutation<ApiResponse<void>, string>({
      query: (menuNo) => ({
        url: `/admin/menu/${menuNo}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Menu'],
    }),

    // 메뉴 정렬 수정 (단건)
    updateMenuSort: builder.mutation<ApiResponse<void>, { menuNo: string; sortSn: number }>({
      query: ({ menuNo, sortSn }) => ({
        url: `/admin/menu/${menuNo}/sort`,
        method: 'PUT',
        body: { sort_sn: sortSn },
      }),
      invalidatesTags: ['Menu'],
    }),

    // 메뉴 이동 (드래그 시 상위/순서 일괄 변경)
    updateMenuMove: builder.mutation<
      ApiResponse<void>, MenuSortUpdateData[]
    >({
      query: (body) => ({
        url: `/admin/menu/move`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Menu'],
    }),

    // 메뉴 이동 (드래그 시 상위/순서만 변경)-old version
    // updateMenuMove: builder.mutation<
    //   ApiResponse<void>,
    //   { menuNo: string; up_menu_no: string | null; sort_sn: number }
    // >({
    //   query: ({ menuNo, up_menu_no, sort_sn }) => ({
    //     url: `/admin/menu/${menuNo}/move`,
    //     method: 'PUT',
    //     body: { up_menu_no: up_menu_no ?? '', sort_sn },
    //   }),
    //   invalidatesTags: ['Menu'],
    // }),
  }),
})

export const {
  useGetAdminNavMenusQuery,
  useGetAuthMenuTreeQuery,
  useSaveAuthMenuMutation,
  useGetMenusQuery,
  useGetMenuByIdQuery,
  useCreateMenuMutation,
  useUpdateMenuMutation,
  useDeleteMenuMutation,
  useUpdateMenuSortMutation,
  useUpdateMenuMoveMutation,
} = menuApi
